import { faCheck, faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from '@/components/CopyToClipboard/useCopyToClipboard';
import { cn } from '../../lib/utils';
import { Button } from '@/components/ui/button';
import { cva } from 'class-variance-authority';

type Variant = 'default' | 'ghost' | 'compact';
type Size = 'default' | 'xs' | 'sm' | 'lg';

interface CopyToClipboardProps {
  value: string;
  variant?: Variant;
  size?: Size;
  className?: string;
  'aria-label'?: string;
}

const containerVariants = cva('flex items-center', {
  variants: {
    variant: {
      default:
        'group gap-4 justify-between border border-solid px-4 py-2 leading-6 rounded-lg border-neutrals-300',
      ghost: 'group gap-4 justify-between py-2 leading-6 rounded-lg',
      compact: 'gap-2',
    },
    size: {
      default: 'h-9',
      xs: 'h-6 text-xs',
      sm: 'h-8 text-sm',
      lg: 'h-12 text-base',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

const valueVariants = cva('', {
  variants: {
    variant: {
      default: 'text-xs tabular-nums',
      compact: 'tabular-nums bg-neutrals-200 p-2 rounded-md',
      ghost: 'text-sm text-neutrals-900',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const iconVariants = cva('', {
  variants: {
    variant: {
      default:
        'text-neutrals-700 opacity-0 group-hover:opacity-100 group-hover:bg-neutrals-200 p-2 rounded-md hover:cursor-pointer',
      ghost: 'text-neutrals-900 p-2 rounded-md hover:cursor-pointer w-4 h-4',
      compact: '',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export function CopyToClipboard({
  value,
  variant = 'default',
  size = 'default',
  className,
  ...props
}: CopyToClipboardProps) {
  const { isCopied, handleCopy } = useCopyToClipboard(value);
  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <div className={cn(containerVariants({ variant, size }), className)}>
        <span className={valueVariants({ variant })}>{value}</span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={props['aria-label'] ?? t('actions.copy')}
          className="h-5 w-5 justify-center bg-transparent hover:bg-transparent"
          onClick={handleCopy}>
          <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(containerVariants({ variant, size }), 'group', className)}>
      <span
        className={valueVariants({ variant })}
        aria-label={props['aria-label'] ?? t('actions.copy')}>
        {value}
      </span>
      <FontAwesomeIcon
        className={iconVariants({ variant })}
        icon={isCopied ? faCheck : faCopy}
        onClick={handleCopy}
      />
    </div>
  );
}
